/**
 * V250: Optimistic Photo Override Store
 * 
 * Allows temporarily overriding the profile photo URL for instant UI updates
 * Used during photo replacement to show new image immediately before upload completes
 */

type OverrideListener = (userId: string, overrideUrl: string | null) => void;

const overrides = new Map<string, string>(); // userId -> override URL
const listeners = new Set<OverrideListener>();

/**
 * Set override URL for a user (shows instantly on profile page)
 */
export function setOverride(userId: string, overrideUrl: string): void {
  overrides.set(userId, overrideUrl);
  if (import.meta.env.DEV) {
    console.log('✅ V250 uiPhotoOverride: Set override for', userId, overrideUrl.substring(0, 50));
  }
  notifyListeners(userId, overrideUrl);
}

/**
 * Get override URL for a user (or null if none)
 */
export function getOverride(userId: string): string | null {
  return overrides.get(userId) || null;
}

/**
 * Clear override for a user (after upload completes or fails)
 */
export function clearOverride(userId: string): void {
  if (overrides.has(userId)) {
    const oldUrl = overrides.get(userId);
    overrides.delete(userId);
    if (import.meta.env.DEV) {
      console.log('✅ V250 uiPhotoOverride: Cleared override for', userId);
    }
    notifyListeners(userId, null);
  }
}

/**
 * Subscribe to override changes
 */
export function subscribe(listener: OverrideListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notifyListeners(userId: string, overrideUrl: string | null): void {
  listeners.forEach(listener => {
    try {
      listener(userId, overrideUrl);
    } catch (error) {
      console.warn('⚠️ V250 uiPhotoOverride: Listener error:', error);
    }
  });
}

